import React, { useState } from 'react';
import { toast } from 'sonner';
import { Trash2, Presentation } from 'lucide-react';
import { axiosInstance as axios } from '../../../context/AuthContext';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';

const EMPTY = { title: '', description: '', presenter_profile_id: '', date: '', time: '', meeting_link: '' };

export function PilotPresentationsCard({ pros, presentations, onChanged }) {
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/pilot/admin/presentations', { ...form, meeting_link: form.meeting_link || null, description: form.description || null });
      toast.success('Presentation scheduled');
      setForm(EMPTY);
      onChanged();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg : d || 'Could not save');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.title}"? Its registrations and ratings are removed too.`)) return;
    try {
      await axios.delete(`/api/pilot/admin/presentations/${p.presentation_id}`);
      onChanged();
    } catch {
      toast.error('Could not remove');
    }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5" data-testid="pilot-presentations-card">
      <h3 className="text-white font-semibold mb-1">Educational presentations</h3>
      <p className="text-slate-400 text-sm mb-4">Pilot families see these once the family pilot is open, register, then rate them afterwards.</p>
      <form onSubmit={create} className="grid sm:grid-cols-2 gap-3 mb-4">
        <Input required placeholder="Title, e.g. Wills & Trusts 101" value={form.title} onChange={set('title')} className="bg-slate-900 border-slate-600 text-white" data-testid="pres-title" />
        <select required value={form.presenter_profile_id} onChange={set('presenter_profile_id')} className="rounded-md bg-slate-900 border border-slate-600 text-white px-3 py-2 text-sm" data-testid="pres-presenter">
          <option value="">Presenter…</option>
          {pros.map(p => <option key={p.profile_id} value={p.profile_id}>{p.name}</option>)}
        </select>
        <Input required type="date" value={form.date} onChange={set('date')} className="bg-slate-900 border-slate-600 text-white" data-testid="pres-date" />
        <Input required placeholder="Time, e.g. 7:00 PM ET" value={form.time} onChange={set('time')} className="bg-slate-900 border-slate-600 text-white" data-testid="pres-time" />
        <Input placeholder="Video meeting link (Zoom, Meet…)" value={form.meeting_link} onChange={set('meeting_link')} className="sm:col-span-2 bg-slate-900 border-slate-600 text-white" data-testid="pres-link" />
        <Textarea placeholder="What families will learn (optional)" value={form.description} onChange={set('description')} className="sm:col-span-2 bg-slate-900 border-slate-600 text-white" data-testid="pres-description" />
        <Button type="submit" disabled={!pros.length} className="sm:col-span-2 bg-blue-600 hover:bg-blue-700" data-testid="pres-submit">
          <Presentation className="h-4 w-4 mr-2" />Schedule presentation
        </Button>
      </form>
      {presentations.map(p => (
        <div key={p.presentation_id} className="flex items-center justify-between py-3 border-b border-slate-700 last:border-0" data-testid={`pres-row-${p.presentation_id}`}>
          <div>
            <p className="text-white">{p.title}</p>
            <p className="text-slate-400 text-xs">{p.presenter_name} · {p.date} {p.time} · {p.registered} registered · {p.surveys} rated</p>
          </div>
          <Button size="sm" variant="ghost" className="text-red-300" onClick={() => remove(p)} data-testid="pres-delete-btn"><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
}
